import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Megaphone, Pencil } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, isRespAdmin } from "@/context/AuthContext";
import { BottomSheet, Empty, DeleteButton, Field, BulkCheck, BulkBar, ConfirmAction } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

export default function Bacheca() {
  const { user } = useAuth();
  const canEdit = isRespAdmin(user);
  const [annunci, setAnnunci] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ titolo: "", testo: "" });
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]);

  const load = useCallback(() => api.get("/annunci").then((r) => setAnnunci(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const apri = (a = null) => {
    setEditId(a?.id || null);
    setForm(a ? { titolo: a.titolo, testo: a.testo } : { titolo: "", testo: "" });
    setSheetOpen(true);
  };

  const salva = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) await api.put(`/annunci/${editId}`, form);
      else await api.post("/annunci", form);
      toast.success(editId ? "Annuncio aggiornato" : "Annuncio pubblicato!");
      setSheetOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/annunci/${editId}`);
      toast.success("Annuncio eliminato");
      setSheetOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const fmtTs = (iso) =>
    new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="bacheca-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Bacheca</h1>
          <p className="text-sm text-slate-500 font-semibold">Annunci per animatori e aiuto animatori</p>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            {annunci.length > 0 && (
              <button className={bulkMode ? "btn-secondary" : "btn-ghost"} onClick={() => { if (bulkMode) { setBulkMode(false); setSelected([]); } else setBulkMode(true); }} data-testid="bacheca-bulk-toggle">
                {bulkMode ? "Annulla selezione" : "Seleziona"}
              </button>
            )}
            <button className="btn-primary" onClick={() => apri()} data-testid="nuovo-annuncio-btn">
              <Plus className="h-4 w-4" /> Nuovo annuncio
            </button>
          </div>
        )}
      </div>

      {bulkMode && (
        <BulkBar count={selected.length} total={annunci.length} testId="bacheca-bulk-bar"
          onSelectAll={() => setSelected(selected.length === annunci.length ? [] : annunci.map((a) => a.id))}
          onClear={() => { setBulkMode(false); setSelected([]); }}
          actions={
            <ConfirmAction onConfirm={async () => {
              try {
                const { data } = await api.post("/annunci/bulk/delete", { ids: selected });
                toast.success(`${data.deleted} annunci eliminati`);
                setBulkMode(false); setSelected([]); load();
              } catch (err) { toast.error(errMsg(err)); }
            }} title={`Eliminare ${selected.length} annunci?`} testId="bacheca-bulk-delete-confirm">
              <button type="button" disabled={selected.length === 0} className="btn-danger disabled:opacity-40 !py-2" data-testid="bacheca-bulk-delete-btn">
                Elimina {selected.length > 0 && `(${selected.length})`}
              </button>
            </ConfirmAction>
          } />
      )}

      {annunci.length === 0 ? (
        <Empty icon={Megaphone} text="Nessun annuncio in bacheca" sub={canEdit ? "Pubblica il primo annuncio per gli animatori" : "Gli annunci dei Responsabili compariranno qui"} />
      ) : (
        <div className="space-y-3 max-w-3xl">
          {annunci.map((a, i) => {
            const isSel = selected.includes(a.id);
            const toggle = () => setSelected((s) => s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id]);
            return (
              <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
                onClick={() => bulkMode && canEdit && toggle()}
                className={`card-app p-5 ${bulkMode && canEdit ? "cursor-pointer" : ""} ${bulkMode && isSel ? "ring-2 ring-amber-400" : ""}`}
                data-testid={`annuncio-card-${a.id}`}>
                <div className="flex items-start gap-3">
                  {bulkMode && canEdit && <BulkCheck checked={isSel} onClick={toggle} testId={`annuncio-check-${a.id}`} />}
                  <div className="h-10 w-10 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                    <Megaphone className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h2 className="font-display font-extrabold text-slate-900">{a.titolo}</h2>
                      {canEdit && !bulkMode && (
                        <button className="btn-ghost !py-1.5 !px-3 text-xs shrink-0" onClick={() => apri(a)} data-testid={`modifica-annuncio-${a.id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Modifica
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 font-medium whitespace-pre-wrap mt-1">{a.testo}</p>
                    <p className="text-xs text-slate-400 font-semibold mt-3">
                      {a.autore_nome} · {fmtTs(a.created_at)}{a.updated_at && " · modificato"}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <BottomSheet open={sheetOpen} onOpenChange={setSheetOpen} title={editId ? "Modifica annuncio" : "Nuovo annuncio"} testId="annuncio-sheet">
        <form onSubmit={salva}>
          <Field label="Titolo">
            <input className="input-app" required value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="annuncio-titolo-input" />
          </Field>
          <Field label="Testo dell'annuncio">
            <textarea className="input-app" rows={5} required value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })} data-testid="annuncio-testo-input" />
          </Field>
          <button className="btn-primary w-full" disabled={saving} data-testid="annuncio-submit-btn">{saving ? "Salvataggio..." : editId ? "Salva modifiche" : "Pubblica"}</button>
          {editId && (
            <div className="mt-3 flex justify-end">
              <DeleteButton onConfirm={elimina} entity="questo annuncio" testId="annuncio-delete-btn" />
            </div>
          )}
        </form>
      </BottomSheet>
    </div>
  );
}
